import { Form, message } from "antd";
import { TextField } from "@mui/material";
import { useState } from "react";
import DocumentTitle from "react-document-title";
import { useSelector } from "react-redux";
import { generatePath, Link, useNavigate } from "react-router";
import { useCurrentUser } from "@/data/hooks";
import MobileHeader from "@/views/Components/MobileHeader";
import { RoutePaths } from "@/views/routes";
import Avatar from "boring-avatars";
import Button from "@/views/Components/Button";
import { analyticsLogEvent } from "@/data/firebase/analytics";
import { selectIsSignedIn } from "@/data/redux/user/selectors";
import Spin from "@/views/Components/Spin";
import { updateAccountEmail } from "@/data/firebase/auth";
import { FirebaseError } from "firebase/app";
import SubHeader from "@/views/Components/SubHeader";
import BackButton from "@/views/Components/MobileHeader/BackButton";

interface EmailForm {
  email: string;
  newEmail: string;
  password: string;
}

export default function EditEmail() {
  const isSignedIn = useSelector(selectIsSignedIn);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, profileLoaded } = useCurrentUser();
  const [form] = Form.useForm<EmailForm>();
  const [messageApi, contextHolder] = message.useMessage();

  const onFinish = async (values: EmailForm) => {
    setLoading(true);
    if (!user?.uid) {
      messageApi.error("Not authorized");
      navigate(generatePath(RoutePaths.account));
    }
    try {
      await updateAccountEmail(values.email, values.newEmail, values.password);

      analyticsLogEvent("account_email_edited");
      message.success("Email updated");
      navigate(generatePath(RoutePaths.account));
    } catch (e) {
      console.log(e);
      if (e instanceof FirebaseError) {
        const err: FirebaseError = e as FirebaseError;
        const errorMap: Record<string, string> = {
          "Firebase: Error (auth/wrong-password).": "Wrong password",
          "Firebase: Error (auth/email-already-in-use).": "Invalid email",
        };
        if (errorMap[err.message]) {
          messageApi.error(errorMap[err.message]);
        } else {
          console.log(err.message);
          messageApi.error("Unknown authorization error, try again later");
        }
      } else {
        messageApi.error("Unknown error, try again later");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isSignedIn) {
    navigate(RoutePaths.login);
    return <></>;
  }

  if (!profileLoaded) {
    return <Spin />;
  }

  const formValues: EmailForm = {
    email: user?.email || "",
    newEmail: "",
    password: "",
  };
  console.log("Form values", formValues);

  return (
    <DocumentTitle title="Edit email">
      <>
        {contextHolder}
        <MobileHeader title="Edit email">
          <Button link onClick={() => form.submit()} loading={loading}>
            Save
          </Button>
          <Link to={RoutePaths.account} className="px-4">
            Cancel
          </Link>
        </MobileHeader>
        <SubHeader>
          <div className="flex justify-start">
            <BackButton link={RoutePaths.account} />
            <div className="h-5"></div>
          </div>
        </SubHeader>
        <div className="flex flex-col items-center gap-8 p-8 text-center">
          <div className="wish-bg-light-blue w-min rounded-full p-3">
            <Avatar name={user?.uid || ""} size={88} variant={"beam"} />
          </div>

          <Form
            initialValues={formValues}
            onFinish={onFinish}
            disabled={loading}
            form={form}
          >
            <Form.Item<EmailForm> name="email">
              <TextField className="w-72" label="Current email" disabled />
            </Form.Item>

            <Form.Item<EmailForm>
              name="newEmail"
              rules={[{ required: true, type: "email" }]}
            >
              <TextField className="w-72" label="New email" />
            </Form.Item>

            <Form.Item<EmailForm> name="password" rules={[{ required: true }]}>
              <TextField
                className="w-72"
                label="Your current password"
                type="password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                shape="round"
                size={"large"}
                block
                htmlType="submit"
                loading={loading}
                className="hidden! md:inline-flex!"
                full
              >
                Save
              </Button>
            </Form.Item>
          </Form>
        </div>
      </>
    </DocumentTitle>
  );
}
